import { useCallback, useMemo, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import {
  useCreateDebounceHandler,
  useDebouncedCallback,
} from "../../../../common/hooks/use_debounced_callback";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { ContratoLista } from "../../domain/models/contrato_lista";
import { useContratoFilters } from "../hooks/contrato/filter";
import { useContratoPagination } from "../hooks/contrato/pagination";
import { ContratoForm, ContratoSchema } from "../schemas/Contrato/contrato";
import { zodResolver } from "@hookform/resolvers/zod";
import { ContratoServicoLista } from "../../domain/models/contrato_servico_lista";
import {
  ContratoServicoForm,
  ContratoServicoSchema,
} from "../schemas/Contrato/contrato_servico";
import { useContratoServicoPagination } from "../hooks/contrato_servico/pagination";
import { useContratoServicoFilters } from "../hooks/contrato_servico/filter";
import {
  ContratoArquivoForm,
  ContratoArquivoSchema,
} from "../schemas/Contrato/contrato_arquivo";
import { ContratoArquivoLista } from "../../domain/models/contrato_arquivo_lista";
import { useContratoArquivoPagination } from "../hooks/contrato_arquivo/pagination";
import { useContratoArquivoFilters } from "../hooks/contrato_arquivo/filter";
import { ContratoInformacoesLista } from "../../domain/models/contrato_informacoes_lista";
import { useContratoInformacoesFilters } from "../hooks/contrato_informacoes/filter";
import { useContratoInformacoesPagination } from "../hooks/contrato_informacoes/pagination";
import {
  ContratoInformacoesForm,
  ContratoInformacoesSchema,
} from "../schemas/Contrato/contrato_informacoes";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { MedicaoContratoItem } from "../../domain/models/medicaoContrato";

type LoadContratoFuncState = {
  IdContrato?: number;
  page?: number;
  rowsPerPage?: number;
};
type LoadServicoFuncState = {
  IdContrato?: number;
  IdContratoServico?: number;
  page?: number;
  rowsPerPage?: number;
};
type LoadArquivoFuncState = {
  IdContrato?: number;
  IdContratoArquivo?: number;
  page?: number;
  rowsPerPage?: number;
};
type LoadInformacoesFuncState = {
  IdContrato?: number;
  IdContratoInformacoes?: number;
  Data?: Date;
  DescInformacoes?: string;
  page?: number;
  rowsPerPage?: number;
};

export const useContratoCadastroViewModel = () => {
  const {
    commons: { getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase },
    terceirizacao: {
      getAllListagemContratoUseCase,
      createContratoUseCase,
      updateContratoUseCase,
      getAllListagemContratoServicoUseCase,
      createContratoServicoUseCase,
      updateContratoServicoUseCase,
      deleteContratoServicoUseCase,
      getAllListagemContratoArquivoUseCase,
      createContratoArquivoUseCase,
      updateContratoArquivoUseCase,
      deleteContratoArquivoUseCase,
      getAllListagemContratoInformacoesUseCase,
      createContratoInformacoesUseCase,
      deleteContratoInformacoesUseCase,
      updateContratoInformacoesUseCase,
      downloadContratoArquivoUseCase,
    },
  } = useDependencyInjection();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [selectedContrato, setSelectedContrato] = useState<ContratoLista>();
  const [IdContrato, setIdContrato] = useState<number>(0);
  const {
    pushErrorNotification,
    pushWarnNotification,
    pushInfoNotification,
    setCustomLoading,
  } = usePageLoader();
  const filters = useContratoFilters();
  const pagination = useContratoPagination();

  const hookForm = useForm<ContratoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ContratoSchema),
    defaultValues: {
      ContratoAssinado: 2,
      Status: 0,
      StatusPcmat: 2,
      StatusPcmso: 2,
    },
  });
  //#region Servico
  const [openServico, setOpenServico] = useState<boolean>(false);
  const [servicoDeleteModal, setServicoDeleteModal] = useState<boolean>(false);
  const [ListaServico, setListaServico] = useState<ContratoServicoLista[]>([]);
  const [selectedServico, setSelectedServico] =
    useState<ContratoServicoLista>();
  const [loadingServico, setLoadingServico] = useState<boolean>(false);

  const filtersServico = useContratoServicoFilters();
  const paginationServico = useContratoServicoPagination();

  const hookFormServico = useForm<ContratoServicoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ContratoServicoSchema),
    defaultValues: {},
  });
  //#endregion

  //#region Informacoes
  const [openInformacoes, setOpenInformacoes] = useState<boolean>(false);
  const [informacoesDeleteModal, setInformacoesDeleteModal] =
    useState<boolean>(false);
  const [ListaInformacoes, setListaInformacoes] = useState<
    ContratoInformacoesLista[]
  >([]);
  const [selectedInformacoes, setSelectedInformacoes] =
    useState<ContratoInformacoesLista>();
  const [loadingInformacoes, setLoadingInformacoes] = useState<boolean>(false);

  const filtersInformacoes = useContratoInformacoesFilters();
  const paginationInformacoes = useContratoInformacoesPagination();
  const debounceHandler = useCreateDebounceHandler(750);

  const hookFormInformacoes = useForm<ContratoInformacoesForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ContratoInformacoesSchema),
    defaultValues: {},
  });
  //#endregion

  //#region Arquivo
  const [openArquivo, setOpenArquivo] = useState<boolean>(false);
  const [ArquivoDeleteModal, setArquivoDeleteModal] = useState<boolean>(false);
  const [ListaArquivo, setListaArquivo] = useState<ContratoArquivoLista[]>([]);
  const [selectedArquivo, setSelectedArquivo] =
    useState<ContratoArquivoLista>();
  const [loadingArquivo, setLoadingArquivo] = useState<boolean>(false);

  const filtersArquivo = useContratoArquivoFilters();
  const paginationArquivo = useContratoArquivoPagination();

  const hookFormArquivo = useForm<ContratoArquivoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(ContratoArquivoSchema),
    defaultValues: {},
  });
  const [openDeleteArquivoModal, setOpenDeleteArquivoModal] =
    useState<boolean>(false);
  const [limparArquivo, setLimparArquivo] = useState<string>();
  const [arquivo, setArquivo] = useState<string>();

  //#endregion
  const { loadCentroCustoDropDown } = useDropDownLoader();

  const [openMedicao, setOpenMedicao] = useState<boolean>(false);
  const [selectedMedicoes, setSelectedMedicoes] = useState<
    MedicaoContratoItem[]
  >([]);

  // #region Funções de carregamento
  const loadContratoListaState = useCallback(
    async (funcState?: LoadContratoFuncState) => {
      try {
        const filters = useContratoFilters.getState();
        const pagination = useContratoPagination.getState();

        const filterID = evaluateParamValue(
          funcState?.IdContrato,
          filters.IdContrato,
        );
        const page = funcState?.page ?? pagination.page;
        const rows = funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllListagemContratoUseCase.getAllListagemContrato(
          {
            IdContrato: filterID,
          },
          page,
          rows,
        );
        const contrato =
          getAllListagemContratoUseCase.items &&
          getAllListagemContratoUseCase.items.length > 0
            ? getAllListagemContratoUseCase.items[0]
            : null;
        if (contrato) {
          setSelectedContrato(getAllListagemContratoUseCase.items[0]);

          hookForm.reset({
            ContratoAssinado: Number(contrato.ContratoAssinado),
            DataAte: contrato.DataAte,
            DataDe: contrato.DataDe,
            Escopo: contrato.Escopo,
            IdCentroCusto: contrato.IdCentroCusto,
            IdContrato: contrato.IdContrato,
            IdFornecedor: contrato.IdFornecedor,
            NumeroContrato: String(contrato.NumeroContrato),
            Status: contrato.Status,
            StatusPcmat: contrato.StatusPcmat,
            StatusPcmso: contrato.StatusPcmso,
            ValorContrato: contrato.ValorContrato,
            ValorMedido: contrato.ValorMedido,
            ValorRestante: contrato.ValorRestante,
          });
        }
        pagination.setRowsPerPage(getAllListagemContratoUseCase.pageSize);
        pagination.setTotalItems(getAllListagemContratoUseCase.totalItems);
      } catch (e: unknown) {
        throw new Error("Falha ao buscar contratos!");
      }
    },
    [getAllListagemContratoUseCase, hookForm],
  );

  const loadServicoListaState = useCallback(
    async (funcState?: LoadServicoFuncState) => {
      try {
        setLoadingServico(true);
        const filterIdServico = evaluateParamValue(
          funcState?.IdContratoServico,
          filtersServico.IdContratoServico,
        );
        const IdContrato = funcState?.IdContrato ?? filtersServico.IdContrato;
        const page = funcState?.page ?? paginationServico.page;
        const rows = funcState?.rowsPerPage ?? paginationServico.rowsPerPage;

        await getAllListagemContratoServicoUseCase.getAllListagemContratoServico(
          {
            IdContrato: IdContrato,
            IdContratoServico: filterIdServico,
          },
          page,
          rows,
        );
        setListaServico(getAllListagemContratoServicoUseCase.items);
        paginationServico.setRowsPerPage(
          getAllListagemContratoServicoUseCase.pageSize,
        );
        paginationServico.setTotalItems(
          getAllListagemContratoServicoUseCase.totalItems,
        );
        setLoadingServico(false);
        hookFormServico.reset({
          IdContrato: IdContrato,
          Detalhamento: "",
          IdContratoServico: 0,
          IdProdutoServico: 0,
          Quantidade: 0,
          ValorTotal: 0,
          ValorUnitario: null,
        });
      } catch (e: unknown) {
        throw new Error("Falha ao buscar contrato de servico!");
      }
    },
    [
      filtersServico.IdContrato,
      filtersServico.IdContratoServico,
      getAllListagemContratoServicoUseCase,
      hookFormServico,
      paginationServico,
    ],
  );

  const loadArquivoListaState = useCallback(
    async (funcState?: LoadArquivoFuncState) => {
      try {
        setLoadingArquivo(true);
        const filterIdArquivo = evaluateParamValue(
          funcState?.IdContratoArquivo,
          filtersArquivo.IdContratoArquivo,
        );
        const IdContrato = funcState?.IdContrato ?? filtersArquivo.IdContrato;
        const page = funcState?.page ?? paginationArquivo.page;
        const rows = funcState?.rowsPerPage ?? paginationArquivo.rowsPerPage;

        await getAllListagemContratoArquivoUseCase.getAllListagemContratoArquivo(
          {
            IdContrato: IdContrato,
            IdContratoArquivo: filterIdArquivo,
          },
          page,
          rows,
        );

        setListaArquivo(getAllListagemContratoArquivoUseCase.items);
        paginationArquivo.setRowsPerPage(
          getAllListagemContratoArquivoUseCase.pageSize,
        );
        paginationArquivo.setTotalItems(
          getAllListagemContratoArquivoUseCase.totalItems,
        );
        setLoadingArquivo(false);
        hookFormArquivo.reset({
          IdContrato: IdContrato,
          IdContratoArquivo: 0,
          Arquivo: null,
          DataVencimento: null,
          Descricao: "",
          Nome: "",
          Obsoleto: 1,
        });
      } catch (e: unknown) {
        throw new Error("Falha ao buscar contrato de arquivo!");
      }
    },
    [
      filtersArquivo.IdContrato,
      filtersArquivo.IdContratoArquivo,
      getAllListagemContratoArquivoUseCase,
      hookFormArquivo,
      paginationArquivo,
    ],
  );

  const loadInformacoesListaState = useCallback(
    async (funcState?: LoadInformacoesFuncState) => {
      try {
        setLoadingInformacoes(true);
        const filterIdInformacoes = evaluateParamValue(
          funcState?.IdContratoInformacoes,
          filtersInformacoes.IdContratoInformacoes,
        );
        const IdContrato =
          funcState?.IdContrato ?? filtersInformacoes.IdContrato;
        const filteredData = evaluateParamValue(
          funcState?.Data,
          filtersInformacoes.Data,
        );
        const filteredDescInformacoes = evaluateParamValue(
          funcState?.DescInformacoes,
          filtersInformacoes.DescInformacoes,
        );
        const page = funcState?.page ?? paginationInformacoes.page;
        const rows =
          funcState?.rowsPerPage ?? paginationInformacoes.rowsPerPage;

        await getAllListagemContratoInformacoesUseCase.getAllListagemContratoInformacoes(
          {
            IdContrato: IdContrato,
            IdContratoInformacoes: filterIdInformacoes,
            Data: filteredData,
            DescInformacoes: filteredDescInformacoes,
          },
          page,
          rows,
        );

        setListaInformacoes(getAllListagemContratoInformacoesUseCase.items);
        paginationInformacoes.setRowsPerPage(
          getAllListagemContratoInformacoesUseCase.pageSize,
        );
        paginationInformacoes.setTotalItems(
          getAllListagemContratoInformacoesUseCase.totalItems,
        );
        setLoadingInformacoes(false);
        hookFormInformacoes.reset({
          IdContrato: IdContrato,
          IdContratoInformacoes: 0,
          Data: undefined,
          DescInformacoes: "",
          IdFuncionario: 0,
        });
      } catch (e: unknown) {
        throw new Error("Falha ao buscar contrato de Informacoes!");
      }
    },
    [
      filtersInformacoes.Data,
      filtersInformacoes.DescInformacoes,
      filtersInformacoes.IdContrato,
      filtersInformacoes.IdContratoInformacoes,
      getAllListagemContratoInformacoesUseCase,
      hookFormInformacoes,
      paginationInformacoes,
    ],
  );

  //#endregion

  // #region useEffect

  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      if (id) {
        setIdContrato(Number(id));
        jobs.push([]);
        jobs.push(loadContratoListaState({ IdContrato: Number(id) }));
        jobs.push(loadServicoListaState({ IdContrato: Number(id) }));
        jobs.push(loadArquivoListaState({ IdContrato: Number(id) }));
        jobs.push(loadInformacoesListaState({ IdContrato: Number(id) }));
      }
      await loadCentroCustoDropDown((list) =>
        useContratoFilters.setState({ centroDeCustoLista: list }),
      );
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      setLoadingArquivo(true);
      setLoadingInformacoes(true);
      setLoadingServico(true);

      const jobs = [];

      if (id) {
        setIdContrato(Number(id));
        jobs.push([]);

        jobs.push(loadContratoListaState({ IdContrato: Number(id) }));
        jobs.push(loadServicoListaState({ IdContrato: Number(id) }));
        jobs.push(loadArquivoListaState({ IdContrato: Number(id) }));
        jobs.push(loadInformacoesListaState({ IdContrato: Number(id) }));
      }
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
      );
    } finally {
      setLoading(false);
      setLoadingArquivo(false);
      setLoadingInformacoes(false);
      setLoadingServico(false);
    }
  }, [
    id,
    loadArquivoListaState,
    loadContratoListaState,
    loadInformacoesListaState,
    loadServicoListaState,
    pushErrorNotification,
  ]);
  //#endregion

  // #region Eventos

  const handleContratoSubmit = useCallback(
    async (form: ContratoForm) => {
      try {
        if (IdContrato) {
          await updateContratoUseCase.updateContrato(
            IdContrato,
            form.Status ?? null,
            form.NumeroContrato ?? null,
            form.Escopo ?? null,
            form.DataDe ?? null,
            form.DataAte ?? null,
            form.IdCentroCusto ?? null,
            form.IdFornecedor ?? null,
            form.StatusPcmat ?? null,
            form.StatusPcmso ?? null,
            form.ValorContrato ?? null,
            form.ValorMedido ?? null,
            form.ValorRestante ?? null,
            String(form.ContratoAssinado) ?? null,
          );
          pushInfoNotification("Sucesso", "Contrato atualizado com sucesso!", {
            isAutoFinish: true,
          });
        } else {
          const idItem = await createContratoUseCase.createContrato(
            form.Status ?? null,
            form.NumeroContrato ?? null,
            form.Escopo ?? null,
            form.DataDe ?? null,
            form.DataAte ?? null,
            form.IdCentroCusto ?? null,
            form.IdFornecedor ?? null,
            form.StatusPcmat ?? null,
            form.StatusPcmso ?? null,
            form.ValorContrato ?? null,
            form.ValorMedido ?? null,
            form.ValorRestante ?? null,
            String(form.ContratoAssinado) ?? null,
          );
          setIdContrato(Number(idItem));
          pushInfoNotification("Sucesso", "Contrato criado com sucesso!", {
            isAutoFinish: true,
          });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar/atualizar Contrato!"),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      IdContrato,
      createContratoUseCase,
      pushErrorNotification,
      pushInfoNotification,
      updateContratoUseCase,
    ],
  );

  //#region Servico
  const totaisServico = useMemo(() => {
    const total = ListaServico.reduce(
      (acc, curr) => acc + (curr.ValorTotal || 0),
      0,
    );
    const medido = ListaServico.reduce(
      (acc, curr) => acc + (curr.ValorMedido || 0),
      0,
    );
    return { total, medido };
  }, [ListaServico]);

  const handleServico = useCallback(async () => {
    if (IdContrato > 0 || selectedContrato) {
      setServicoDeleteModal(false);
      setOpenServico(true);
      setSelectedServico(undefined);
      let id = IdContrato > 0 ? IdContrato : selectedServico?.IdContrato;
      hookFormServico.reset({
        IdContrato: id,
        IdContratoServico: 0,
        Detalhamento: "",
        IdProdutoServico: 0,
        Quantidade: 0,
        ValorTotal: 0,
        ValorUnitario: 0,
      });
    } else {
      setServicoDeleteModal(false);
      setOpenServico(false);
      setSelectedServico(undefined);
      return pushWarnNotification(
        "Atenção",
        "Para incluir um documento é necessário salvar o Contrato.",
      );
    }
  }, [
    IdContrato,
    hookFormServico,
    pushWarnNotification,
    selectedContrato,
    selectedServico?.IdContrato,
  ]);

  const handleServicoSubmit = useCallback(
    async (form: ContratoServicoForm) => {
      try {
        setSaving(true);
        if (IdContrato && IdContrato > 0) {
          if (form.IdContratoServico) {
            setOpenServico(false);
            await updateContratoServicoUseCase.updateContratoServico(
              form.IdContratoServico,
              IdContrato,
              Number(form.IdProdutoServico),
              form.Detalhamento ?? null,
              form.Quantidade ?? null,
              form.ValorUnitario ?? null,
              form.ValorTotal ?? null,
            );
            setOpenServico(false);
            pushInfoNotification(
              "Sucesso",
              "Contrato de serviço atualizado com sucesso!",
              { isAutoFinish: true },
            );
          } else {
            await createContratoServicoUseCase.createContratoServico(
              IdContrato,
              Number(form.IdProdutoServico),
              form.Detalhamento ?? null,
              form.Quantidade ?? null,
              form.ValorUnitario ?? null,
              form.ValorTotal ?? null,
            );
            pushInfoNotification(
              "Sucesso",
              "Contrato de serviço criado com sucesso!",
              { isAutoFinish: true },
            );
            setOpenServico(false);
          }
          loadServicoListaState({ IdContrato: IdContrato });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar contrato servico!"),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      IdContrato,
      createContratoServicoUseCase,
      loadServicoListaState,
      pushErrorNotification,
      pushInfoNotification,
      updateContratoServicoUseCase,
    ],
  );

  const handleEdicaoServicoOpenState = useCallback(
    (IdContratoServico: number | null, openState: boolean) => {
      if (openState) setOpenServico(true);

      if (IdContratoServico) {
        const itemParaEditar = ListaServico.find(
          (x) => x.IdContratoServico === IdContratoServico,
        );
        setSelectedServico(itemParaEditar);
      } else {
        hookFormServico.reset();
      }
    },
    [ListaServico, hookFormServico],
  );

  const handleDeleteServicoModalOpenState = useCallback(
    (IdContratoServico: number | null, openState: boolean) => {
      if (openState) {
        if (IdContrato && ListaServico && IdContratoServico) {
          setSelectedServico(
            ListaServico.find((x) => x.IdContratoServico === IdContratoServico),
          );
        } else {
          setSelectedServico(undefined);
        }
      }
      setServicoDeleteModal(openState);
    },
    [IdContrato, ListaServico],
  );

  const handleDeleteConfirmaServico = useCallback(async () => {
    if (selectedServico) {
      try {
        setSaving(true);
        await deleteContratoServicoUseCase.deleteContratoServico(
          selectedServico.IdContratoServico,
        );
        await loadServicoListaState({ IdContrato: IdContrato });
        pushInfoNotification("Sucesso", "Contrato deletado com sucesso!", {
          isAutoFinish: true,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"),
        );
      } finally {
        setSaving(false);
        setServicoDeleteModal(false);
      }
    }
  }, [
    selectedServico,
    deleteContratoServicoUseCase,
    loadServicoListaState,
    IdContrato,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  //#endregion
  //#region Informacoes
  const handleInformacoes = useCallback(async () => {
    if (IdContrato > 0 || selectedContrato) {
      setInformacoesDeleteModal(false);
      setOpenInformacoes(true);
      setSelectedInformacoes(undefined);
      let id = IdContrato > 0 ? IdContrato : selectedInformacoes?.IdContrato;
      hookFormInformacoes.reset({
        IdContrato: id,
        IdContratoInformacoes: 0,
        Data: undefined,
        DescInformacoes: "",
        IdFuncionario: 0,
      });
    } else {
      setInformacoesDeleteModal(false);
      setOpenInformacoes(false);
      setSelectedInformacoes(undefined);
      return pushWarnNotification(
        "Atenção",
        "Para incluir um documento é necessário salvar o Contrato.",
      );
    }
  }, [
    IdContrato,
    hookFormInformacoes,
    pushWarnNotification,
    selectedContrato,
    selectedInformacoes?.IdContrato,
  ]);

  const handleInformacoesSubmit = useCallback(
    async (form: ContratoInformacoesForm) => {
      try {
        setSaving(true);
        if (IdContrato && IdContrato > 0) {
          if (form.IdContratoInformacoes) {
            await updateContratoInformacoesUseCase.updateContratoInformacoes(
              form.IdContratoInformacoes,
              IdContrato,
              form.DescInformacoes ?? null,
              null,
              form.IdFuncionario ?? null,
              form.Data ?? null,
            );
            setOpenInformacoes(false);
            pushInfoNotification(
              "Sucesso",
              "Contrato de informacoes atualizado com sucesso!",
              { isAutoFinish: true },
            );
          } else {
            await createContratoInformacoesUseCase.createContratoInformacoes(
              IdContrato,
              form.DescInformacoes ?? null,
              null,
              form.IdFuncionario ?? null,
              form.Data ?? null,
            );
            pushInfoNotification(
              "Sucesso",
              "Contrato de informacoes criado com sucesso!",
              { isAutoFinish: true },
            );
            setOpenInformacoes(false);
          }
          loadInformacoesListaState({ IdContrato: IdContrato });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar contrato informacoes!"),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      IdContrato,
      createContratoInformacoesUseCase,
      loadInformacoesListaState,
      pushErrorNotification,
      pushInfoNotification,
      updateContratoInformacoesUseCase,
    ],
  );

  const handleEdicaoInformacoesOpenState = useCallback(
    (IdContratoInformacoes: number | null, openState: boolean) => {
      if (openState) setOpenInformacoes(true);

      if (IdContratoInformacoes) {
        const itemParaEditar = ListaInformacoes.find(
          (x) => x.IdContratoInformacoes === IdContratoInformacoes,
        );
        setSelectedInformacoes(itemParaEditar);
      } else {
        hookFormInformacoes.reset();
      }
    },
    [ListaInformacoes, hookFormInformacoes],
  );

  const handleDeleteInformacoesModalOpenState = useCallback(
    (IdContratoInformacoes: number | null, openState: boolean) => {
      if (openState) {
        if (IdContrato && ListaInformacoes && IdContratoInformacoes) {
          setSelectedInformacoes(
            ListaInformacoes.find(
              (x) => x.IdContratoInformacoes === IdContratoInformacoes,
            ),
          );
        } else {
          setSelectedInformacoes(undefined);
        }
      }
      setInformacoesDeleteModal(openState);
    },
    [IdContrato, ListaInformacoes],
  );

  const handleDeleteConfirmaInformacoes = useCallback(async () => {
    if (selectedInformacoes) {
      try {
        setSaving(true);
        await deleteContratoInformacoesUseCase.deleteContratoInformacoes(
          selectedInformacoes.IdContratoInformacoes,
        );
        await loadInformacoesListaState({ IdContrato: IdContrato });
        pushInfoNotification(
          "Sucesso",
          "Contrato de informacoes deletado com sucesso!",
          { isAutoFinish: true },
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"),
        );
      } finally {
        setSaving(false);
        setInformacoesDeleteModal(false);
      }
    }
  }, [
    selectedInformacoes,
    deleteContratoInformacoesUseCase,
    loadInformacoesListaState,
    IdContrato,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleClear = useCallback(async () => {
    try {
      setLoading(true);
      useContratoInformacoesFilters.getState().clearFilters();
      useContratoInformacoesPagination.setState({ page: 1 });
      await loadInformacoesListaState({
        IdContrato: IdContrato,
      });
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao atualizar pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [IdContrato, loadInformacoesListaState, pushErrorNotification]);

  const handleDataChange = useDebouncedCallback(
    debounceHandler,
    (data: Date | null) => {
      useContratoInformacoesFilters.setState({ Data: data });
      return async () => {
        try {
          setLoading(true);
          useContratoInformacoesPagination.setState({ page: 1 });
          await loadInformacoesListaState({
            Data: data ?? undefined,
            IdContrato: IdContrato,
            page: 1,
          });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao atualizar pagina!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadInformacoesListaState, pushErrorNotification],
  );

  const handleDescricaoChange = useDebouncedCallback(
    debounceHandler,
    (descricao: string | null) => {
      useContratoInformacoesFilters.setState({ DescInformacoes: descricao });
      return async () => {
        try {
          setLoading(true);
          useContratoInformacoesPagination.setState({ page: 1 });
          await loadInformacoesListaState({
            DescInformacoes: String(descricao),
            IdContrato: IdContrato,
            page: 1,
          });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao atualizar pagina!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadInformacoesListaState, pushErrorNotification],
  );

  //#endregion

  //#region Arquivo
  const handleArquivo = useCallback(async () => {
    if (IdContrato > 0 || selectedContrato) {
      setArquivoDeleteModal(false);
      setOpenArquivo(true);
      setSelectedArquivo(undefined);
      let id = IdContrato > 0 ? IdContrato : selectedArquivo?.IdContrato;
      hookFormArquivo.reset({
        IdContrato: id,
        IdContratoArquivo: 0,
        Arquivo: null,
        DataVencimento: null,
        Descricao: "",
        Nome: "",
        Obsoleto: 0,
      });
    } else {
      setArquivoDeleteModal(false);
      setOpenArquivo(false);
      setSelectedArquivo(undefined);
      return pushWarnNotification(
        "Atenção",
        "Para incluir um documento é necessário salvar o Contrato.",
      );
    }
  }, [
    IdContrato,
    hookFormArquivo,
    pushWarnNotification,
    selectedArquivo?.IdContrato,
    selectedContrato,
  ]);

  const handleArquivoSubmit = useCallback(
    async (form: ContratoArquivoForm) => {
      try {
        setCustomLoading(true);
        if (IdContrato && IdContrato > 0) {
          if (form.IdContratoArquivo) {
            await updateContratoArquivoUseCase.updateContratoArquivo(
              form.IdContratoArquivo,
              IdContrato,
              form.Descricao ?? null,
              form.Nome ?? null,
              form.Arquivo ?? null,
              form.Obsoleto ?? null,
              form.DataVencimento ?? null,
              form.limparArquivo1 ?? null,
            );
            setOpenArquivo(false);
            pushInfoNotification(
              "Sucesso",
              "Contrato de arquivo atualizado com sucesso!",
              { isAutoFinish: true },
            );
          } else {
            await createContratoArquivoUseCase.createContratoArquivo(
              IdContrato,
              form.Descricao ?? null,
              form.Nome ?? null,
              form.Arquivo ?? null,
              form.Obsoleto ?? null,
              form.DataVencimento ?? null,
            );
            pushInfoNotification(
              "Sucesso",
              "Contrato de arquivo criado com sucesso!",
              { isAutoFinish: true },
            );
            setOpenArquivo(false);
          }
          await loadArquivoListaState({ IdContrato: IdContrato });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar contrato arquivo!"),
        );
      } finally {
        setSaving(false);
        setCustomLoading(false);
      }
    },
    [
      IdContrato,
      createContratoArquivoUseCase,
      loadArquivoListaState,
      pushErrorNotification,
      pushInfoNotification,
      setCustomLoading,
      updateContratoArquivoUseCase,
    ],
  );

  const handleEdicaoArquivoOpenState = useCallback(
    (IdContratoArquivo: number | null, openState: boolean) => {
      if (openState) setOpenArquivo(true);

      if (IdContratoArquivo) {
        const itemParaEditar = ListaArquivo.find(
          (x) => x.IdContratoArquivo === IdContratoArquivo,
        );
        setSelectedArquivo(itemParaEditar);
      } else {
        hookFormArquivo.reset();
      }
    },
    [ListaArquivo, hookFormArquivo],
  );

  const handleDeleteArquivoModalOpenState = useCallback(
    (IdContratoArquivo: number | null, openState: boolean) => {
      if (openState) {
        if (IdContrato && ListaArquivo && IdContratoArquivo) {
          setSelectedArquivo(
            ListaArquivo.find((x) => x.IdContratoArquivo === IdContratoArquivo),
          );
        } else {
          setSelectedArquivo(undefined);
        }
      }
      setArquivoDeleteModal(openState);
    },
    [IdContrato, ListaArquivo],
  );

  const handleDeleteConfirmaArquivo = useCallback(async () => {
    if (selectedArquivo) {
      try {
        setSaving(true);
        await deleteContratoArquivoUseCase.deleteContratoArquivo(
          selectedArquivo.IdContratoArquivo,
        );
        await loadArquivoListaState({ IdContrato: IdContrato });
        pushInfoNotification("Sucesso", "Contrato deletado com sucesso!", {
          isAutoFinish: true,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"),
        );
      } finally {
        setSaving(false);
        setArquivoDeleteModal(false);
      }
    }
  }, [
    selectedArquivo,
    deleteContratoArquivoUseCase,
    loadArquivoListaState,
    IdContrato,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  //#region ARQUIVOS

  const handleDownload = useCallback(
    async (idContratoArquivo: number, nomeArquivo: string) => {
      if (ListaArquivo) {
        setCustomLoading(true);
        try {
          await downloadContratoArquivoUseCase.downloadContratoArquivo(
            idContratoArquivo,
            nomeArquivo,
          );
          setCustomLoading(false);
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao fazer download do arquivo!"),
          );
        }
      }
    },
    [
      ListaArquivo,
      downloadContratoArquivoUseCase,
      pushErrorNotification,
      setCustomLoading,
    ],
  );

  const handleArquivoDeleteOpenState = useCallback(
    (
      limparArquivos: string | null,
      arquivo: string | null,
      openState: boolean,
    ) => {
      if (openState && limparArquivos && arquivo) {
        setOpenDeleteArquivoModal(openState);
        setLimparArquivo(limparArquivos);
        setArquivo(arquivo);
      } else {
        setOpenDeleteArquivoModal(openState);
        setLimparArquivo(undefined);
        setArquivo(undefined);
      }
    },
    [],
  );

  const handleDeleteFile = useCallback(
    (limparArquivos: string, arquivo: string) => {
      if (!saving) {
        hookFormArquivo.setValue(limparArquivos as any, true);
        hookFormArquivo.setValue(arquivo as any, null);
        setSelectedArquivo((prev) =>
          prev ? { ...prev, [arquivo]: null, Nome: null } : undefined,
        );

        if (limparArquivo === "limparArquivo1")
          hookFormArquivo.setValue("limparArquivo1", true);
      }
    },
    [saving, limparArquivo, hookFormArquivo],
  );

  const handleConfirmarDeletarArquivo = useCallback(() => {
    if (limparArquivo && arquivo) {
      handleDeleteFile(limparArquivo, arquivo);
      setOpenDeleteArquivoModal(false);
      setArquivo(undefined);
      setLimparArquivo(undefined);
    }
  }, [arquivo, handleDeleteFile, limparArquivo]);

  //#endregion

  //#endregion

  const handleVoltarContrato = useCallback(() => {
    navigate(`/Contrato_lista/`);
  }, [navigate]);

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (IdContrato) {
          changeHistory.handleOpenChangeHistory(
            IdContrato,
            LogTipoTabela.CONTRATO,
            true,
          );
        }
      }
    },
    [IdContrato, changeHistory],
  );

  const handleMedicaoOpenState = useCallback(
    (openState: boolean) => {
      setOpenMedicao(openState);

      if (openState) {
        const listaMedicoes = selectedContrato?.Medicoes ?? [];
        setSelectedMedicoes(listaMedicoes);
      } else {
        setSelectedMedicoes([]);
      }
    },
    [selectedContrato],
  );

  //#endregion
  return {
    changeHistory,
    loading,
    errorMessage,
    saving,
    pagination,
    filters,
    hookForm,
    hookFormArquivo,
    selectedContrato,
    IdContrato,
    paginationServico,
    paginationArquivo,
    paginationInformacoes,
    hookFormServico,
    selectedServico,
    loadingServico,
    servicoDeleteModal,
    openServico,
    ListaServico,
    ListaArquivo,
    ListaInformacoes,
    totaisServico,
    selectedArquivo,
    selectedInformacoes,
    loadingArquivo,
    ArquivoDeleteModal,
    informacoesDeleteModal,
    openArquivo,
    openDeleteArquivoModal,
    hookFormInformacoes,
    openInformacoes,
    loadingInformacoes,
    filtersInformacoes,
    selectedMedicoes,
    openMedicao,
    handleOpenChangeHistory,
    setOpenInformacoes,
    setOpenArquivo,
    setOpenServico,
    handleReload,
    handleVoltarContrato,
    handleContratoSubmit,
    handleServico,
    handleServicoSubmit,
    handleEdicaoServicoOpenState,
    handleDeleteServicoModalOpenState,
    handleDeleteConfirmaServico,
    handleDeleteConfirmaArquivo,
    handleDeleteArquivoModalOpenState,
    handleEdicaoArquivoOpenState,
    handleArquivoSubmit,
    handleArquivo,
    handleDownload,
    handleArquivoDeleteOpenState,
    handleDeleteFile,
    handleConfirmarDeletarArquivo,
    handleInformacoes,
    handleInformacoesSubmit,
    handleEdicaoInformacoesOpenState,
    handleDeleteInformacoesModalOpenState,
    handleDeleteConfirmaInformacoes,
    handleDescricaoChange,
    handleDataChange,
    handleClear,
    handleMedicaoOpenState,
    setOpenMedicao,
    setSelectedMedicoes
  };
};
